import React, { useCallback, useEffect, useState } from 'react';
import { Link, useHistory, useLocation, useParams } from 'react-router-dom';
import styled from 'styled-components';
import Card from 'components/Card';
import DataTable, { CellPrimary, CellSecondary } from 'components/DataTable';
import EnhancedThemeProvider from 'components/EnhancedThemeProvider';
import ExportModal from 'components/ExportModal';
import Select from 'components/FormFields/Select';
import Toggle from 'components/FormFields/Toggle';
import PageHeaderIcon from 'components/PageHeaderIcon';
import QuickActions from 'components/QuickActions';
import SearchInput from 'components/SearchInput';
import Toaster from 'components/Toaster';
import { useSearchInput, useUpdateQuery } from 'hooks/useSearchInput';
import { useGetMembershipDivisions } from 'pages/app/AnnualTickets/Members/gql/useGetMembershipDivisions';
import {
  useCheckinRegistration,
  useGetEventRegistrations,
  useUnCheckRegistration,
} from './gql';
import { useGetRegistrationTickets } from './gql/useGetRegistrationTickets';
import useRegistrationsExport from './hooks/useRegistrationsExport';
import { getDriverName } from './RegistrationsDesktop';

// Page Container
const PageContainer = styled.div`
  padding: 24px;
  max-width: 1400px;
  margin: 0 auto;
  min-height: 100vh;
  background: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.background || '#0F172A'
      : '#FFFFFF'};

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

// Header components
const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const HeaderTitle = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.textGray?.primary || '#ffffff'
      : props.theme.colors?.blueBlack || '#000033'};
  margin: 0;
  font-family: Arial, -apple-system, system-ui, BlinkMacSystemFont, 'Segoe UI',
    Roboto, 'Helvetica Neue', Ubuntu, sans-serif;
`;

const HeaderSubtitle = styled.p`
  font-size: 14px;
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
  margin: 4px 0 0 0;
`;

// Content wrapper
const ContentWrapper = styled.div`
  padding: 32px;
  font-family: Arial, -apple-system, system-ui, BlinkMacSystemFont, 'Segoe UI',
    Roboto, 'Helvetica Neue', Ubuntu, sans-serif;
  width: 100%;
  box-sizing: border-box;
  position: relative;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

// Page Header Section
const PageHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 24px;
  gap: 16px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const DashboardLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 300px;
  gap: 30px;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
`;

const MainContent = styled.div`
  min-width: 0;
`;

const Sidebar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  background: ${(props) =>
    props.signed
      ? props.theme.name === 'dark'
        ? 'rgba(34, 197, 94, 0.2)'
        : 'rgba(34, 197, 94, 0.1)'
      : props.theme.name === 'dark'
      ? 'rgba(239, 68, 68, 0.2)'
      : 'rgba(239, 68, 68, 0.1)'};
  color: ${(props) => (props.signed ? '#22C55E' : '#EF4444')};
`;

const RegistrationLink = styled(Link)`
  color: ${(props) => props.theme?.colors?.primary || '#FA4616'};
  font-weight: 500;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const EventInfoCard = styled.div`
  padding: 16px;
  background: ${(props) =>
    props.theme.name === 'dark'
      ? 'rgba(255, 255, 255, 0.05)'
      : 'rgba(0, 0, 0, 0.02)'};
  border-radius: 8px;
  margin-bottom: 16px;
`;

const EventInfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgba(0, 0, 0, 0.05)'};

  &:last-child {
    border-bottom: none;
  }
`;

const EventInfoLabel = styled.span`
  font-size: 14px;
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
`;

const EventInfoValue = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.textGray?.primary || '#ffffff'
      : props.theme.colors?.blueBlack || '#000033'};
`;

export const TrackRegistrationsDetail = () => {
  const { id } = useParams();
  const eventId = Number(id);

  const [registrations, setRegistrations] = useState([]);
  const [count, setCount] = useState(0);
  const [ticketFilter, setTicketFilter] = useState(null);
  const [division, setDivision] = useState(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [toasterData, setToasterData] = useState(null);

  const history = useHistory();
  const { pathname } = useLocation();
  const isTrackAdmin = pathname.includes('/admin-track');
  const isEmployeeAdmin = pathname.includes('/admin-employee');
  const routePrefix = isTrackAdmin
    ? '/admin-track'
    : isEmployeeAdmin
    ? '/admin-employee'
    : '/admin';

  const { input } = useSearchInput();
  const currentPage = input.page ? Number(input.page) : 1;
  const updateQuery = useUpdateQuery();

  // Fetch data
  const { data: tickets } = useGetRegistrationTickets();
  const { data, loading, refetch } = useGetEventRegistrations(
    ticketFilter,
    division
  );
  const { data: divisions } = useGetMembershipDivisions(
    ticketFilter && ticketFilter !== 'All Registrations' ? ticketFilter : null
  );

  // Check-in hooks
  const checkinRegistration = useCheckinRegistration();
  const unCheckRegistration = useUnCheckRegistration();

  // Export hook
  const { startExport, subscribeToProgress, getDownloadUrl, isExporting } =
    useRegistrationsExport();

  const event = data?.getEvent;

  useEffect(() => {
    if (data?.getEventRegistrations) {
      setRegistrations(data.getEventRegistrations.results || []);
      setCount(data.getEventRegistrations.count || 0);
    }
  }, [data]);

  // Handle search
  const handleSearch = useCallback(
    (searchTerm) => {
      const currentQueryString = input.queryString || '';
      if (searchTerm !== currentQueryString) {
        updateQuery({ queryString: searchTerm, page: 1 });
      }
    },
    [updateQuery, input.queryString]
  );

  // Handle pagination
  const handlePageChange = (page) => {
    const totalPages = Math.ceil(count / 15);
    if (page < 1 || page > totalPages) return;
    updateQuery({ page });
  };

  // Handle check-in toggle
  const handleCheckInToggle = async (registration) => {
    try {
      if (registration.is_checked) {
        await unCheckRegistration(registration.id);
      } else {
        await checkinRegistration(registration.id);
      }
      refetch();
    } catch (error) {
      console.error('Check-in error:', error);
    }
  };

  // Handle ticket filter change
  const handleTicketFilterChange = (e) => {
    const value = e.target.value;
    setTicketFilter(value || null);
    setDivision(null);
    updateQuery({ page: 1 });
  };

  // Handle division filter change
  const handleDivisionChange = (e) => {
    const value = e.target.value;
    setDivision(value || null);
    updateQuery({ page: 1 });
  };

  // Handle export
  const handleExport = useCallback(
    async (format) => {
      setShowExportModal(false);

      setToasterData({
        title: `Exporting to ${format}...`,
        message: 'Preparing your export file',
        type: 'info',
        persistent: true,
      });

      try {
        const filters = {};
        if (ticketFilter) {
          filters.ticket_id = Number(ticketFilter);
        }
        if (division) {
          filters.division = division;
        }
        if (input.queryString) {
          filters.search = input.queryString;
        }

        const jobId = await startExport(eventId, filters, format);

        subscribeToProgress(jobId, {
          onProgress: (status) => {
            if (status.progress && status.recordCount) {
              setToasterData({
                title: `Exporting to ${format}...`,
                message: `Processing ${status.recordCount} registrations (${status.progress}%)`,
                type: 'info',
                persistent: true,
              });
            }
          },
          onComplete: async (data) => {
            try {
              const downloadUrl = await getDownloadUrl(jobId);

              setToasterData({
                title: 'Export Ready!',
                message: `${
                  data.recordCount || 0
                } registrations exported to ${format}`,
                type: 'success',
                persistent: true,
                action: {
                  text: `Download ${format}`,
                  icon: 'download',
                  onClick: () => {
                    window.open(downloadUrl, '_blank');
                  },
                },
              });
            } catch (error) {
              setToasterData({
                title: 'Download Error',
                message: 'Failed to generate download link. Please try again.',
                type: 'error',
              });
            }
          },
          onError: (error) => {
            let errorMessage =
              'An error occurred while exporting. Please try again.';

            if (error.message?.includes('permission')) {
              errorMessage =
                'You do not have permission to export registrations';
            } else if (error.message?.includes('not found')) {
              errorMessage = 'Export not found or has expired';
            }

            setToasterData({
              title: 'Export Failed',
              message: errorMessage,
              type: 'error',
            });
          },
        });
      } catch (error) {
        setToasterData({
          title: 'Export Failed',
          message: 'Unable to start export. Please try again.',
          type: 'error',
        });
      }
    },
    [
      eventId,
      ticketFilter,
      division,
      input.queryString,
      startExport,
      subscribeToProgress,
      getDownloadUrl,
    ]
  );

  // Ticket filter options
  const ticketOptions = [
    { value: '', label: 'All Registrations' },
    ...(tickets?.getRegistrationTickets?.map((ticket) => ({
      value: ticket.id,
      label: ticket.name,
    })) || []),
  ];

  // Division filter options
  const divisionOptions = [
    { value: '', label: 'All Divisions' },
    ...(divisions?.getMembershipDivisions?.map((div) => ({
      value: div,
      label: div,
    })) || []),
  ];

  // DataTable columns
  const columns = [
    {
      field: 'is_checked',
      header: 'Check In',
      render: (registration) => (
        <Toggle
          checked={!!registration.is_checked}
          onChange={() => handleCheckInToggle(registration)}
        />
      ),
    },
    {
      field: 'purchase_date',
      header: 'Date',
      mobileSubtitle: true,
    },
    {
      field: 'name',
      header: 'Name',
      mobileTitle: true,
      render: (registration) => (
        <RegistrationLink
          to={`${routePrefix}/registrationDetail/registration/${registration.pass_id}`}
        >
          <CellPrimary>
            {getDriverName(registration.data, registration.user)}
          </CellPrimary>
        </RegistrationLink>
      ),
    },
    {
      field: 'registration',
      header: 'Registration Name',
      render: (registration) => (
        <CellSecondary>{registration.registration?.name || ''}</CellSecondary>
      ),
    },
    {
      field: 'user',
      header: 'Purchaser Name',
      render: (registration) =>
        registration.user
          ? `${registration.user.last_name}, ${registration.user.first_name}`
          : '',
    },
    {
      field: 'division',
      header: 'Division',
    },
    {
      field: 'price',
      header: 'Total',
      render: (registration) => registration?.registration?.price || '',
    },
    {
      field: 'is_signed',
      header: 'Agreements',
      render: (registration) => (
        <StatusBadge signed={registration.is_signed}>
          {registration.is_signed ? 'Signed' : 'Not Signed'}
        </StatusBadge>
      ),
    },
  ];

  // DataTable actions
  const actions = [
    {
      type: 'view',
      title: 'View Details',
      mobileLabel: 'View',
      to: (registration) =>
        `${routePrefix}/registrationDetail/registration/${registration.pass_id}`,
    },
  ];

  const totalPages = Math.ceil(count / 15);

  // Show divisions filter only if a ticket type is selected and divisions exist
  const showDivisionsFilter =
    ticketFilter &&
    divisions?.getMembershipDivisions &&
    divisions.getMembershipDivisions.length > 0;

  return (
    <EnhancedThemeProvider>
      <PageContainer>
        {/* Header Card */}
        <Card style={{ marginBottom: '30px' }}>
          <HeaderContainer>
            <PageHeaderIcon icon="events" />
            <div>
              <HeaderTitle>
                {event?.name || 'Event'} - Registrations
              </HeaderTitle>
              {event && (
                <HeaderSubtitle>
                  {event.isMultiDay
                    ? `${event.fullMonth} ${event.listDates}`
                    : event.fullDate}
                  {event.track?.name && ` - ${event.track.name}`}
                </HeaderSubtitle>
              )}
            </div>
          </HeaderContainer>
        </Card>

        {/* Dashboard Layout */}
        <DashboardLayout>
          <MainContent>
            {/* Content Card */}
            <Card noPadding>
              <ContentWrapper>
                {/* Page Header with Search and Filters */}
                <PageHeader>
                  <SearchInput
                    onSearch={handleSearch}
                    placeholder="Search registrations..."
                    debounceMs={500}
                  />
                  <div style={{ width: '200px', flexShrink: 0 }}>
                    <Select
                      value={ticketFilter || ''}
                      onChange={handleTicketFilterChange}
                      options={ticketOptions}
                    />
                  </div>
                  {showDivisionsFilter && (
                    <div style={{ width: '180px', flexShrink: 0 }}>
                      <Select
                        value={division || ''}
                        onChange={handleDivisionChange}
                        options={divisionOptions}
                      />
                    </div>
                  )}
                </PageHeader>

                {/* DataTable */}
                <DataTable
                  columns={columns}
                  data={registrations}
                  loading={loading}
                  keyField="id"
                  emptyMessage="No registrations found"
                  emptySubtext="Try adjusting your search or filters"
                  actions={actions}
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              </ContentWrapper>
            </Card>
          </MainContent>

          <Sidebar>
            {/* Event Info Card */}
            {event && (
              <Card>
                <EventInfoCard>
                  <EventInfoRow>
                    <EventInfoLabel>Event</EventInfoLabel>
                    <EventInfoValue>{event.name}</EventInfoValue>
                  </EventInfoRow>
                  <EventInfoRow>
                    <EventInfoLabel>Date</EventInfoLabel>
                    <EventInfoValue>
                      {event.isMultiDay
                        ? `${event.fullMonth} ${event.listDates}`
                        : event.fullDate}
                    </EventInfoValue>
                  </EventInfoRow>
                  <EventInfoRow>
                    <EventInfoLabel>Track</EventInfoLabel>
                    <EventInfoValue>{event.track?.name || '-'}</EventInfoValue>
                  </EventInfoRow>
                  <EventInfoRow>
                    <EventInfoLabel>Total Registrations</EventInfoLabel>
                    <EventInfoValue>{count}</EventInfoValue>
                  </EventInfoRow>
                </EventInfoCard>
              </Card>
            )}

            {/* Quick Actions */}
            <QuickActions
              title="Quick Actions"
              actions={[
                {
                  label: 'Export Report',
                  icon: (
                    <svg
                      width="16"
                      height="16"
                      fill="currentColor"
                      viewBox="0 0 16 16"
                    >
                      <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z" />
                      <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708l3 3z" />
                    </svg>
                  ),
                  onClick: () => setShowExportModal(true),
                },
                {
                  label: 'Back to Registrations',
                  icon: (
                    <svg
                      width="16"
                      height="16"
                      fill="currentColor"
                      viewBox="0 0 16 16"
                    >
                      <path
                        fillRule="evenodd"
                        d="M15 8a.5.5 0 0 0-.5-.5H2.707l3.147-3.146a.5.5 0 1 0-.708-.708l-4 4a.5.5 0 0 0 0 .708l4 4a.5.5 0 0 0 .708-.708L2.707 8.5H14.5A.5.5 0 0 0 15 8z"
                      />
                    </svg>
                  ),
                  onClick: () => history.goBack(),
                },
              ]}
            />
          </Sidebar>
        </DashboardLayout>

        {/* Export Modal */}
        <ExportModal
          isOpen={showExportModal}
          onClose={() => setShowExportModal(false)}
          onExport={handleExport}
          isExporting={isExporting}
          recordCount={count}
          dataType="registrations"
        />

        {/* Toaster */}
        {toasterData && (
          <Toaster
            isOpen={!!toasterData}
            onClose={() => setToasterData(null)}
            title={toasterData.title}
            message={toasterData.message}
            type={toasterData.type}
            duration={toasterData.persistent ? 25000 : 10000}
            persistent={toasterData.persistent}
            action={toasterData.action}
          />
        )}
      </PageContainer>
    </EnhancedThemeProvider>
  );
};

export default TrackRegistrationsDetail;
